import { useLocationStore } from '@/stores/useLocationStore';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useId } from 'react';
import { HelpCircle, ChevronDown, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LocationSelectContent } from './LocationSelectContent';

interface LocationPickerProps {
    label?: string;
    placeholder?: string;
    compact?: boolean;
    /** Show helper text below the location (default: false for compact, true otherwise) */
    showHelperText?: boolean;
}

/**
 * LocationPicker Component
 *
 * Allows users to select a pickup location for their rental.
 * Updated per GUD-SEARCH-002/003/004: Label as "Pickup Location",
 * include helper text, and tooltip explaining the pickup concept.
 */
export const LocationPicker = ({
    label = "Pickup Location",
    placeholder = "Select pickup location",
    compact = false,
    showHelperText = !compact
}: LocationPickerProps) => {
    const { location } = useLocationStore();
    const helperId = useId();

    if (compact) {
        return (
            <Popover className="relative">
                <PopoverButton
                    className={cn(
                        'group flex min-h-[44px] w-full items-center rounded-xl text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        location && 'bg-secondary/80 px-2.5 py-2 text-foreground hover:bg-secondary'
                    )}
                    aria-label={`${label}. ${location ? location.name : placeholder}. Choose the Stork Rentals location where you'll collect and return your equipment.`}
                >
                    {location ? (
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/80 text-primary shadow-sm">
                                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-[0.625rem] font-bold uppercase tracking-[0.18em] text-primary/80">
                                    Pickup in
                                </span>
                                <span className="block truncate font-display text-sm leading-tight text-foreground">
                                    {location.name}
                                </span>
                            </span>
                        </span>
                    ) : (
                        <span className={cn('text-xs text-foreground truncate', !location && 'text-muted-foreground/60')}>
                            {placeholder}
                        </span>
                    )}
                    <ChevronDown className="ml-1 h-3 w-3 flex-shrink-0 text-muted-foreground/60 transition-transform duration-150 group-data-[open]:rotate-180" aria-hidden="true" />
                </PopoverButton>

                <PopoverPanel
                    anchor={{ to: 'bottom start', gap: 8 }}
                    className="z-[200] w-72 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                    {({ close }) => (
                        <LocationSelectContent onSelect={close} />
                    )}
                </PopoverPanel>
            </Popover>
        );
    }

    return (
        <Popover className="relative">
            <PopoverButton
                className="group flex min-h-[44px] w-full flex-col items-start rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-describedby={showHelperText ? helperId : undefined}
            >
                {label && (
                    <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-foreground">
                        <span>{label}</span>
                        <span title="Choose the Stork Rentals location where you'll collect and return your equipment.">
                            <HelpCircle
                                className="h-3.5 w-3.5 text-muted-foreground/60"
                                aria-hidden="true"
                            />
                        </span>
                    </span>
                )}
                <span className="flex items-center gap-1">
                    <span className={cn(location ? 'max-w-[180px] truncate font-display text-base text-foreground' : 'max-w-[150px] truncate text-sm text-muted-foreground', !location && 'text-muted-foreground/60')}>
                        {location ? location.name : placeholder}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/50 transition-transform duration-150 group-data-[open]:rotate-180" aria-hidden="true" />
                </span>
                {/* Helper text - shown below the selected location */}
                {showHelperText && (
                    <span id={helperId} className="mt-0.5 text-xs text-muted-foreground">
                        Where you&apos;ll collect your items
                    </span>
                )}
            </PopoverButton>

            <PopoverPanel
                anchor={{ to: 'bottom start', gap: 16 }}
                className="z-[200] w-72 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
                {({ close }) => (
                    <LocationSelectContent onSelect={close} />
                )}
            </PopoverPanel>
        </Popover>
    );
};
